import type { ReactNode } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import { EmptyState } from "../../feedback/EmptyState";
import { ScrollContainer } from "../../layout/ScrollContainer";
import { styled } from "../../../../lib/styled";

const ClickableTableRow = styled(TableRow)<{ $clickable: boolean }>(({ $clickable }) => ({
  cursor: $clickable ? "pointer" : undefined,
}));

/** A cell of a column given a width: it keeps to it, and what is wider is cut off by the cell's own
 *  content (a link's ellipsis), not by growing the table. */
const SizedCell = styled(TableCell)<{ $width?: number | string }>(({ $width }) =>
  $width === undefined ? {} : { width: $width, maxWidth: $width, overflow: "hidden" }
);

export interface DataTableColumn<Row> {
  key: string;
  header: ReactNode;
  /** Renders one cell for this column, for a given row. */
  cell: (row: Row) => ReactNode;
  align?: "left" | "right" | "center";
  /** The column's width, in px or any CSS length. Without it the column takes the room its content
   *  needs. Give one to a column of long text, so the text is cut with an ellipsis. */
  width?: number | string;
}

/** Row-selection wiring. Selectability is a caller decision (e.g. only "ready" rows) — the
 *  table has no domain knowledge of what makes a row selectable. */
export interface DataTableSelection<Row> {
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  isSelectable: (row: Row) => boolean;
  onToggleAll: () => void;
  allSelected: boolean;
  anySelected: boolean;
}

interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  getRowId: (row: Row) => string;
  selection?: DataTableSelection<Row>;
  emptyMessage?: ReactNode;
  /** Navigates or opens a detail view for the clicked row. Clicks inside the selection checkbox
   *  cell or a column keyed "actions" do not trigger it, so row actions keep working underneath. */
  onRowClick?: (row: Row) => void;
}

/**
 * A dense data table for internal admin surfaces: column definitions + rows, with an optional
 * leading checkbox-selection column. Closed shape — pagination and toolbars are composed by the
 * caller around it (see Documents), not owned by the table itself.
 */
export function DataTable<Row>({
  columns,
  rows,
  getRowId,
  selection,
  emptyMessage = "No rows to show.",
  onRowClick,
}: DataTableProps<Row>) {
  if (rows.length === 0) {
    return <EmptyState message={emptyMessage} />;
  }

  const selectableRows = selection ? rows.filter(selection.isSelectable) : [];

  return (
    <ScrollContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {selection && (
              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                {selectableRows.length > 0 && (
                  <Checkbox
                    size="small"
                    checked={selection.allSelected}
                    indeterminate={selection.anySelected && !selection.allSelected}
                    onChange={selection.onToggleAll}
                  />
                )}
              </TableCell>
            )}
            {columns.map((col) => (
              <SizedCell key={col.key} align={col.align} $width={col.width}>
                {col.header}
              </SizedCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const id = getRowId(row);
            return (
              <ClickableTableRow
                key={id}
                selected={selection?.selectedIds.has(id)}
                hover
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                $clickable={!!onRowClick}
              >
                {selection && (
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    {selection.isSelectable(row) && (
                      <Checkbox
                        size="small"
                        checked={selection.selectedIds.has(id)}
                        onChange={() => selection.onToggle(id)}
                      />
                    )}
                  </TableCell>
                )}
                {columns.map((col) => (
                  <SizedCell
                    key={col.key}
                    align={col.align}
                    $width={col.width}
                    onClick={col.key === "actions" ? (e) => e.stopPropagation() : undefined}
                  >
                    {col.cell(row)}
                  </SizedCell>
                ))}
              </ClickableTableRow>
            );
          })}
        </TableBody>
      </Table>
    </ScrollContainer>
  );
}
